import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import * as dateFnsLocale from 'date-fns/locale';
import enLocale from 'date-fns/locale/en-US';
import heLocale from 'date-fns/locale/he';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const localeMap = {
  en: enLocale,
  he: heLocale
};

const OnlineBookingLocaleContext = createContext({
  currentLocale: 'en',
  localeData: {},
  t: () => '',
  dateFnsLocaleData: dateFnsLocale.enUS
});

const emittedWarnings = {};

function getPath(path, obj) {
  if (!path || typeof path !== 'string') return null;

  return path.split('.').reduce((agg, segment) => agg && agg[segment] ? agg[segment] : null, obj);
}

function interpolate(message, genericTerms = [], params = {}) {
  message = genericTerms.reduce((msg, { placeholder, term }) => msg.toString().replace(placeholder, term), message || '');
  message = genericTerms.reduce((msg, { placeholder, term }) => msg.toString().replace(placeholder.toLowerCase(), term.toLowerCase()), message);
  return message.toString()
    .replace(
      /([^{}]?){{(?!{)([^{}]+)}}(?!})/gm,
      (match, prefix, paramName) => {
        const interpolator = (params || {})[paramName];

        if (interpolator == null) return prefix;
        if (typeof interpolator === 'function') return `${prefix}${interpolator(paramName) || ''}`;

        return `${prefix}${interpolator}`;
      }
    );
}

export function useOnlineBookingLocale() { return useContext(OnlineBookingLocaleContext); }

export const OnlineBookingLocale = ({ locales, current, children }) => {
  const currentLocale = current || 'en';

  const { dateFnsLocaleData, localeData, t } = useMemo(() => {
    const locale = (locales || {})[currentLocale];
    const errorKey = `__MISSING_LOCALE__:${currentLocale}`;
    if (!locale && process.env.NODE_ENV === 'development' && !emittedWarnings[errorKey]) {
      console.warn(`Missing locale: ${currentLocale}`);
      emittedWarnings[errorKey] = true;
      return { localeData: {}, t: () => 'MISSING_LOCALE' };
    }

    const { translations, ...localeData } = locale || {};

    const localeModule = dateFnsLocale;
    const dateFnsLocaleData = (localeModule[currentLocale.replace(/\W/g, '')]) || (localeModule[currentLocale.substr(0, 2)]);

    return {
      dateFnsLocaleData,
      localeData,
      currentLocale,
      locales,
      t: (key, params) => {
        const translation = getPath(key, locale?.translations || {});
        const errorKey = `${currentLocale}:${key}`;
        if (!translation && process.env.NODE_ENV === 'development' && !emittedWarnings[errorKey]) {
          console.warn(`Missing translation: ${errorKey}`);
          emittedWarnings[errorKey] = true;
          return key;
        }

        return interpolate(translation || '', localeData.genericTerms, params);
      }
    }
  }, [locales, currentLocale]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[currentLocale]}>
      <OnlineBookingLocaleContext.Provider value={{ currentLocale, localeData, t, dateFnsLocaleData, locales }}>
        {children}
      </OnlineBookingLocaleContext.Provider>
    </LocalizationProvider>
  )
};
